/**
 * Integrations API service
 * Handles integration management API calls
 */

import { apiClient } from "./client";
import { Integration } from "./types";

export interface IntegrationStatus {
  connected: boolean;
  last_sync?: string;
  error?: string;
}

export interface ConnectResponse {
  auth_url: string;
}

export interface SyncResponse {
  success: boolean;
  synced_at: string;
}

export const integrationsService = {
  /**
   * Get all integrations
   */
  getAll: () => apiClient.get<Integration[]>("maat-integrations"),

  /**
   * Get integration by ID
   */
  getById: (id: string) =>
    apiClient.get<Integration>("maat-integrations", { id }),

  /**
   * Get integration status
   */
  getStatus: (id: string) =>
    apiClient.get<IntegrationStatus>("maat-integrations", {
      id,
      action: "status",
    }),

  /**
   * Connect integration (start OAuth flow)
   */
  connect: (id: string) =>
    apiClient.post<ConnectResponse>(
      "maat-integrations",
      {},
      { params: { id, action: "connect" } },
    ),

  /**
   * Sync integration data
   */
  sync: (id: string) =>
    apiClient.post<SyncResponse>(
      "maat-integrations",
      {},
      { params: { id, action: "sync" } },
    ),

  /**
   * Create new integration
   */
  create: (data: {
    type: "jira" | "github" | "linear" | "slack" | "azure_devops";
    name: string;
    config: Record<string, unknown>;
  }) => apiClient.post<Integration>("maat-integrations", data),

  /**
   * Update integration
   */
  update: (
    id: string,
    data: {
      name?: string;
      config?: Record<string, unknown>;
      connected?: boolean;
    },
  ) =>
    apiClient.put<Integration>("maat-integrations", data, { params: { id } }),

  /**
   * Delete integration
   */
  delete: (id: string) =>
    apiClient.delete<{ success: boolean }>("maat-integrations", {
      params: { id },
    }),
};
